import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	Signal,
	WritableSignal,
	computed,
	input,
	signal
} from '@angular/core';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { Observable, map } from 'rxjs';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { ImageGalleryDb } from 'lib/cosmos/image-gallery/imageGalleryDb.js';

interface IImageGalleryCategory {
	title: string;
	images: WritableSignal<CosmosDb.ICosmosFileReference[]>;
}

@Component({
    selector: 'mod-image-gallery',
    templateUrl: './image-gallery-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ImageGalleryFormComponent extends TypeCoreComponent<ImageGalleryDb.IImageGalleryItem> implements OnInit {
	public readonly isInDialog = input<boolean>(false);

	public readonly imageGalleryCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 250
	};

	public readonly form = {
		workoutImages: signal<CosmosDb.ICosmosFileReference[]>([]),
		genericImages: signal<CosmosDb.ICosmosFileReference[]>([]),
		dietImages: signal<CosmosDb.ICosmosFileReference[]>([])
	};

	public readonly imageCategories = computed<IImageGalleryCategory[]>(() => {
		const categories: IImageGalleryCategory[] = [
			{
				title: 'Generic',
				images: this.form.genericImages
			},
			{
				title: 'Workout',
				images: this.form.workoutImages
			},
			{
				title: 'Diet',
				images: this.form.dietImages
			}
		];

		return categories;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const form = this.form;
		const sharedData = this.sharedData();

		if (!sharedData) {
			return undefined;
		}

		return this.dependencies.httpService
			.patch<ImageGalleryDb.IImageGalleryItem>({
				type: 'imageGallery',
				patchModel: {
					id: sharedData.data.id,
					dietImages: form.dietImages(),
					workoutImages: form.workoutImages(),
					genericImages: form.genericImages()
				}
			})
			.pipe(
				map((response) => {
					super.notify('Gallery updated');
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			this.form.genericImages.set(sharedData?.data.genericImages ?? []);
			this.form.workoutImages.set(sharedData?.data.workoutImages ?? []);
			this.form.dietImages.set(sharedData?.data.dietImages ?? []);
		};
	}

	getUploadFunction(
		field: Signal<CosmosDb.ICosmosFileReference[]>
	): (file: File) => Observable<CosmosDb.ICosmosFileReference[]> {
		return (file) => {
			return this.dependencies.httpService
				.uploadFile({
					file: file,
					type: 'imageGallery',
					clientImageCompression: 'none'
				})
				.pipe(
					map((response) => {
						return [...field(), response.data];
					})
				);
		};
	}

	handleUploadImage(
		files: CosmosDb.ICosmosFileReference[] | undefined,
		field: WritableSignal<CosmosDb.ICosmosFileReference[]>
	): void {
		if (files) {
			field.set(files);
			const saveHandler = this.saveHandler();

			if (saveHandler) {
				super.subscribeToObservable(saveHandler);
			}
		}
	}
}
